import { formatPrice, formatQuantity } from "@/lib/format";
import { ACCENT, INK, LINE, MUTED, ZEBRA, loadPdfMake, slug } from "@/lib/pdf";

const longDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const rangeText = (range) =>
  range.from === range.to
    ? longDate(range.from)
    : `${longDate(range.from)} – ${longDate(range.to)}`;

const summaryCell = (label, value) => ({
  stack: [
    { text: label, fontSize: 8, color: MUTED, characterSpacing: 0.4 },
    { text: value, fontSize: 13, bold: true, color: INK, margin: [0, 3, 0, 0] },
  ],
});

export const salesPdfFileName = ({ range, branchName }) => {
  const days =
    range.from === range.to ? range.from : `${range.from}_${range.to}`;
  const branch = branchName ? `-${slug(branchName)}` : "";

  return `urun-satislari-${days}${branch}.pdf`;
};

export const buildSalesPdf = async ({
  brandName,
  branchName,
  range,
  search,
  rows,
  totals,
}) => {
  const pdfMake = await loadPdfMake();
  const generatedAt = new Date().toLocaleString("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const listed = rows.reduce(
    (sum, row) => ({
      quantity: sum.quantity + row.quantity,
      kurus: sum.kurus + row.amountKurus,
    }),
    { quantity: 0, kurus: 0 },
  );

  const meta = [
    `Tarih aralığı: ${rangeText(range)}`,
    `Şube: ${branchName ?? "Tüm şubeler"}`,
    ...(search ? [`Arama: “${search}”`] : []),
  ];

  const header = [
    { text: "#", style: "th", alignment: "right" },
    { text: "Ürün", style: "th" },
    { text: "Kategori", style: "th" },
    { text: "Adet", style: "th", alignment: "right" },
    { text: "Tutar", style: "th", alignment: "right" },
  ];

  const body = rows.map((row, index) => [
    { text: String(index + 1), color: MUTED, alignment: "right" },
    { text: row.name, bold: true },
    { text: row.category ?? "—", color: MUTED },
    { text: formatQuantity(row.quantity), alignment: "right" },
    { text: formatPrice(row.amountKurus / 100), alignment: "right" },
  ]);

  const footer = [
    { text: "", border: [false, true, false, false] },
    {
      text: search ? "Listelenen toplam" : "Toplam",
      bold: true,
      colSpan: 2,
      border: [false, true, false, false],
    },
    { text: "" },
    {
      text: formatQuantity(listed.quantity),
      bold: true,
      alignment: "right",
      border: [false, true, false, false],
    },
    {
      text: formatPrice(listed.kurus / 100),
      bold: true,
      alignment: "right",
      border: [false, true, false, false],
    },
  ];

  const doc = {
    pageSize: "A4",
    pageMargins: [40, 48, 40, 52],
    info: {
      title: `Ürün Satışları — ${rangeText(range)}`,
      author: brandName,
      creator: "Noxus",
    },
    defaultStyle: {
      font: "Roboto",
      fontSize: 9.5,
      color: INK,
      lineHeight: 1.15,
    },
    styles: {
      th: { bold: true, fontSize: 8.5, color: MUTED, characterSpacing: 0.3 },
    },
    footer: (page, pages) => ({
      margin: [40, 16, 40, 0],
      columns: [
        { text: `${brandName} · ${generatedAt}`, fontSize: 8, color: MUTED },
        {
          text: `Sayfa ${page} / ${pages}`,
          fontSize: 8,
          color: MUTED,
          alignment: "right",
        },
      ],
    }),
    content: [
      {
        text: brandName.toLocaleUpperCase("tr"),
        fontSize: 9,
        bold: true,
        color: ACCENT,
        characterSpacing: 1,
      },
      {
        text: "Ürün Satışları",
        fontSize: 20,
        bold: true,
        margin: [0, 4, 0, 6],
      },
      { text: meta.join("   ·   "), fontSize: 9, color: MUTED },
      {
        margin: [0, 18, 0, 18],
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              summaryCell("SATIŞ TUTARI", formatPrice(totals.revenue)),
              summaryCell("SATILAN ADET", formatQuantity(totals.quantity)),
              summaryCell("ÜRÜN ÇEŞİDİ", formatQuantity(totals.products)),
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (i) => (i === 1 || i === 2 ? 1 : 0),
          vLineColor: () => LINE,
          paddingLeft: (i) => (i === 0 ? 0 : 14),
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [22, "*", 120, 52, 84],
          body: [header, ...body, footer],
        },
        layout: {
          hLineWidth: (i, node) =>
            i === 1 || i === node.table.body.length - 1 ? 1 : 0,
          hLineColor: () => LINE,
          vLineWidth: () => 0,
          fillColor: (i, node) =>
            i > 0 && i < node.table.body.length - 1 && i % 2 === 0
              ? ZEBRA
              : null,
          paddingTop: () => 5,
          paddingBottom: () => 5,
          paddingLeft: (i) => (i === 0 ? 2 : 6),
          paddingRight: (i, node) =>
            i === node.table.widths.length - 1 ? 2 : 6,
        },
      },
    ],
  };

  return pdfMake.createPdf(doc);
};

export const downloadSalesPdf = async (report) => {
  const pdf = await buildSalesPdf(report);

  await pdf.download(salesPdfFileName(report));
};
